import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DataContext } from '../../Provider/dataProvider';
import { fetchWithAuth } from '../../utils/authUtils';
import {
    buildRevisitPath,
    formatAlertDateTime,
    parseMeta,
    resolveAlertAction,
    revisitSummaryFromMeta,
} from '../../utils/alertUtils';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { cartCount, subscribeCart } from '../../utils/cartStore';
import { FEATURES } from '../../constants/featureAccess';
import { normalizeTierLabel } from '../../constants/membershipPackages';
import '../../css/header.css';
import '../../css/featureGate.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
const APK_DOWNLOAD_URL = `${process.env.PUBLIC_URL || ''}/TamCare.apk`;

const Header = () => {
    const { data, clearAccessToken } = useContext(DataContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [showNotiDropdown, setShowNotiDropdown] = useState(false);
    const [itemsInCart, setItemsInCart] = useState(cartCount());
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isCompact, setIsCompact] = useState(() => window.innerWidth <= 992);
    const [alerts, setAlerts] = useState([]);
    const [alertsError, setAlertsError] = useState('');
    const [followupModal, setFollowupModal] = useState(null);
    const [dueFollowups, setDueFollowups] = useState([]);
    const [followupSubmitting, setFollowupSubmitting] = useState(false);
    const dismissedFollowupLogIdsRef = useRef(new Set());
    const [me, setMe] = useState(null);
    const { access, canUse, loading: accessLoading } = useFeatureAccess();

    useEffect(() => subscribeCart(() => setItemsInCart(cartCount())), []);

    const isLoggedIn = Boolean(data?.id);
    const isElderly = data?.role === 'elderly';
    const isRelative = data?.role === 'relative';
    const isAdmin = data?.role === 'admin';

    const renderNavLink = (to, feature, icon, label) => {
        const locked = feature && (isRelative || isElderly) && (accessLoading || !canUse(feature));
        const className = `tamcare-nav-link ${!locked && isActive(to)} ${locked ? 'tamcare-nav-link--locked' : ''}`;
        if (locked) {
            return (
                <Link
                    to="/membership"
                    className={className}
                    title="Tính năng cần gói hội viên — nhấn để mua gói"
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                    <i className={icon}></i>
                    <span>{label}</span>
                    <i className="fa-solid fa-lock" />
                </Link>
            );
        }
        return (
            <Link to={to} className={className} onClick={() => setIsMobileMenuOpen(false)}>
                <i className={icon}></i>
                <span>{label}</span>
            </Link>
        );
    };

    const membershipBadge = useMemo(() => {
        const label = normalizeTierLabel(me?.tierLabel || access?.tierLabel);
        if (label && label !== 'Thường' && label !== 'Chưa có gói') return `Hội viên ${label}`;
        return 'Hội viên thường';
    }, [me?.tierLabel, access?.tierLabel]);

    const profileInitial = useMemo(() => {
        const sourceName = data?.fullName || data?.username || '';
        return sourceName.trim() ? sourceName.trim().charAt(0).toUpperCase() : '?';
    }, [data?.fullName, data?.username]);

    useEffect(() => {
        if (!isLoggedIn) { setMe(null); return; }
        let aborted = false;
        const controller = new AbortController();
        (async () => {
            try {
                const res = await fetchWithAuth(`${API_BASE}/me`, { signal: controller.signal });
                const json = await res.json().catch(() => ({}));
                if (!aborted && res.ok && json?.success) {
                    setMe(json.data);
                }
            } catch (_) {}
        })();
        return () => { aborted = true; controller.abort(); };
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn) return;
        const refresh = async () => {
            try {
                const res = await fetchWithAuth(`${API_BASE}/me`);
                const json = await res.json().catch(() => ({}));
                if (res.ok && json?.success) setMe(json.data);
            } catch (_) {}
        };
        const handler = () => refresh();
        window.addEventListener('tamcare:profile-updated', handler);
        return () => window.removeEventListener('tamcare:profile-updated', handler);
    }, [isLoggedIn]);

    const loadAlerts = useCallback(async () => {
        if (!isLoggedIn) return;
        if (accessLoading) return;
        if ((isRelative || isElderly) && !canUse(FEATURES.ALERTS)) {
            setAlerts([]);
            setAlertsError('');
            return;
        }
        try {
            setAlertsError('');
            const response = await fetchWithAuth(`${API_BASE}/api/alerts?unread=1&limit=20`);
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Không thể tải thông báo.');
            }
            setAlerts(Array.isArray(payload?.data) ? payload.data : []);
        } catch (err) {
            setAlerts([]);
            setAlertsError(err?.message || 'Không thể tải thông báo.');
        }
    }, [isLoggedIn, accessLoading, isRelative, isElderly, canUse]);

    const loadDueFollowups = useCallback(async () => {
        if (!isLoggedIn || !isElderly) return;
        try {
            const elderlyId = data?.id || data?.profileId;
            if (!elderlyId) return;

            const response = await fetchWithAuth(`${API_BASE}/api/medication-logs/followup-due/${elderlyId}`);
            const payload = await response.json().catch(() => ({}));
            if (response.ok && payload?.success) {
                setDueFollowups(Array.isArray(payload?.data) ? payload.data : []);
            }
        } catch (err) {
            setDueFollowups([]);

        }
    }, [isLoggedIn, isElderly, data?.id, data?.profileId]);

    const unreadCount = alerts.length;

    const followupAlerts = useMemo(() => {
        return alerts
            .map((alert) => ({ ...alert, meta: parseMeta(alert.metaJson) }))
            .filter((alert) => alert?.meta?.kind === 'med_followup' && alert?.meta?.logId);
    }, [alerts]);

    useEffect(() => {
        if (!isLoggedIn) {
            setAlerts([]);
            setAlertsError('');
            setFollowupModal(null);
            setDueFollowups([]);
            dismissedFollowupLogIdsRef.current = new Set();
            return;
        }

        loadAlerts();
        loadDueFollowups();

        const alertsIntervalId = window.setInterval(() => {
            loadAlerts();
        }, 10000);

        const followupsIntervalId = window.setInterval(() => {
            loadDueFollowups();
        }, 10000);

        return () => {
            window.clearInterval(alertsIntervalId);
            window.clearInterval(followupsIntervalId);
        };
    }, [isLoggedIn, loadAlerts, loadDueFollowups]);

    useEffect(() => {
        if (!isLoggedIn) return;
        if (!isElderly) return;
        if (followupModal) return;

        const isDismissed = (logId) => dismissedFollowupLogIdsRef.current.has(Number(logId));

        // Ưu tiên hiển thị từ dueFollowups (polling trực tiếp)
        const firstDue = dueFollowups.find((item) => item?.logId && !isDismissed(item.logId));
        if (firstDue) {
            setFollowupModal({
                meta: { kind: 'med_followup', logId: firstDue.logId },
                alertId: null,
            });
            return;
        }

        // Fallback: hiển thị từ alerts nếu có
        const firstAlert = followupAlerts.find(
            (alert) => alert?.meta?.logId && !isDismissed(alert.meta.logId)
        );
        if (firstAlert) {
            setFollowupModal(firstAlert);
        }
    }, [isLoggedIn, isElderly, followupAlerts, followupModal, dueFollowups]);

    const markAlertRead = async (recipientId) => {
        if (!recipientId) return;
        try {
            await fetchWithAuth(`${API_BASE}/api/alerts/${recipientId}/read`, { method: 'PATCH' });
        } finally {
            loadAlerts();
        }
    };

    const submitFollowupReaction = async (followupAlert, reaction) => {
        if (!followupAlert?.meta?.logId || followupSubmitting) return;
        const logId = Number(followupAlert.meta.logId);
        setFollowupSubmitting(true);
        try {
            const response = await fetchWithAuth(`${API_BASE}/api/medication-logs/${logId}/followup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reaction,
                    alertId: followupAlert.alertId,
                }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Không thể ghi nhận phản ứng.');
            }

            dismissedFollowupLogIdsRef.current.add(logId);
            setDueFollowups((prev) => prev.filter((item) => Number(item.logId) !== logId));
            setFollowupModal(null);

            if (followupAlert.recipientId) {
                await markAlertRead(followupAlert.recipientId);
            }
        } catch (err) {
            setAlertsError(err?.message || 'Không thể ghi nhận phản ứng.');
        } finally {
            setFollowupSubmitting(false);
            await Promise.all([loadAlerts(), loadDueFollowups()]);
        }
    };

    const handleOpenFollowup = (alert) => {
        setFollowupModal(alert);
        setShowNotiDropdown(false);
    };

    const handleAlertClick = (alert, meta) => {
        const action = resolveAlertAction(alert, meta, {
            elderlyId: data?.id || data?.profileId,
        });

        if (action.type === 'followup') {
            handleOpenFollowup({ ...alert, meta });
            return;
        }

        if (action.type === 'revisit') {
            markAlertRead(alert.recipientId);
            setShowNotiDropdown(false);
            navigate(buildRevisitPath(action.recordId, meta || {}));
            return;
        }

        if (action.type === 'medication') {
            markAlertRead(alert.recipientId);
            setShowNotiDropdown(false);
            navigate(`/medication-checklist/${action.elderlyId}`);
            return;
        }
        if (action.type === 'relatives') {
            markAlertRead(alert.recipientId);
            setShowNotiDropdown(false);
            navigate('/my-relatives');
            return;
        }

        markAlertRead(alert.recipientId);
        setShowNotiDropdown(false);
    };

    const renderAlertItem = (alert) => {
        const meta = parseMeta(alert.metaJson);
        const summary = meta?.kind === 'revisit' ? revisitSummaryFromMeta(meta) : '';
        return (
            <button
                key={alert.recipientId}
                type="button"
                className="tamcare-noti-item tamcare-noti-item-button"
                onClick={() => handleAlertClick(alert, meta)}
            >
                <strong>{alert.title}</strong>
                <p>{summary || alert.content}</p>
                <small>{formatAlertDateTime(alert.createdAt)}</small>
            </button>
        );
    };

    const handleLogout = async () => {
        try {
            // Call server to revoke refresh tokens and clear HttpOnly cookie
            await fetchWithAuth(`${API_BASE}/logout`, { method: 'POST' });
        } catch (e) {
            // ignore
        } finally {
            clearAccessToken();
            window.location.href = '/';
        }
    };

    const isActive = (path) => {
        return location.pathname === path ? 'active' : '';
    };

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => setIsCompact(window.innerWidth <= 992);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isMobileMenuOpen) {
            setShowNotiDropdown(false);
            setShowUserDropdown(false);
        }
    }, [isMobileMenuOpen]);

    useEffect(() => {
        if (!isCompact) return undefined;

        const handleClickOutside = () => {
            setShowNotiDropdown(false);
            setShowUserDropdown(false);
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isCompact]);

    return (
        <header className="tamcare-navbar">
            <div className="tamcare-nav-left">
                <Link to="/" className="tamcare-logo">
                    <img
                        src={`${process.env.PUBLIC_URL}/logo_web.png`}
                        alt="TamCare"
                        className="tamcare-brand-logo tamcare-brand-logo--header"
                    />
                    <span>TamCare</span>
                </Link>

                <button
                    className="tamcare-menu-toggle"
                    onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                    aria-label={isMobileMenuOpen ? 'Đóng menu' : 'Mở menu'}
                    aria-expanded={isMobileMenuOpen}
                >
                    <i className={`fa-solid ${isMobileMenuOpen ? 'fa-xmark' : 'fa-bars'}`}></i>
                </button>

                {isCompact && isLoggedIn && !isAdmin && (
                    <Link to="/shop/cart" className="tamcare-cart-btn tamcare-compact-tool" aria-label="Giỏ hàng">
                        <i className="fa-solid fa-cart-shopping"></i>
                        {itemsInCart > 0 && <div className="tamcare-noti-dot">{itemsInCart}</div>}
                    </Link>
                )}

                {isCompact && isLoggedIn && (
                    <div
                        className="tamcare-header-item-wrapper tamcare-compact-noti"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="tamcare-noti-btn"
                            onClick={() => {
                                setShowNotiDropdown((prev) => !prev);
                                setShowUserDropdown(false);
                            }}
                        >
                            <i className="fa-solid fa-bell"></i>
                            {unreadCount > 0 && <div className="tamcare-noti-dot">{unreadCount}</div>}
                        </button>
                        {showNotiDropdown && (
                            <div className="tamcare-dropdown-box">
                                <div className="tamcare-noti-header">Thông báo mới</div>
                                <div className="tamcare-noti-scroll">
                                    {alertsError ? (
                                        <div className="tamcare-noti-item">
                                            <strong>Lỗi</strong>
                                            <p>{alertsError}</p>
                                        </div>
                                    ) : alerts.length === 0 ? (
                                        <div className="tamcare-noti-item">
                                            <strong>Không có thông báo mới</strong>
                                            <p>Hệ thống sẽ hiển thị nhắc uống thuốc và cảnh báo tại đây.</p>
                                        </div>
                                    ) : (
                                        alerts.map((alert) => renderAlertItem(alert))
                                    )}
                                </div>
                                <div className="tamcare-noti-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '8px' }}>
                                    <Link
                                        to="/alerts"
                                        className="tamcare-dropdown-link"
                                        style={{ width: '100%', textAlign: 'center', display: 'block' }}
                                        onClick={() => { setShowNotiDropdown(false); }}
                                    >
                                        <i className="fa-regular fa-bell" style={{ marginRight: 6 }}></i>
                                        Xem chi tiết
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {isCompact && isLoggedIn && (
                    <div
                        className="tamcare-header-item-wrapper tamcare-compact-user"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="tamcare-avatar-btn"
                            aria-label="Tài khoản"
                            onClick={() => {
                                setShowUserDropdown((prev) => !prev);
                                setShowNotiDropdown(false);
                            }}
                        >
                            {me?.avatar ? (
                                <img src={`${API_BASE}${me.avatar}`} alt="" />
                            ) : (
                                <i className="fa-solid fa-user"></i>
                            )}
                        </button>
                        {showUserDropdown && (
                            <div className="tamcare-dropdown-box">
                                <div className="tamcare-profile-info">
                                    <div className="tamcare-avatar-large" style={{ overflow: 'hidden' }}>
                                        {me?.avatar ? (
                                            <img src={`${API_BASE}${me.avatar}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                        ) : (
                                            profileInitial
                                        )}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '700', color: '#2c5282', fontSize: '14px' }}>
                                            {data?.fullName || data?.username}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.4' }}>
                                            {membershipBadge}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ padding: '10px 0' }}>
                                    <Link to="/profile" className="tamcare-dropdown-link" onClick={() => setShowUserDropdown(false)}>
                                        <i className="fa-solid fa-address-card" style={{ color: '#6366f1' }}></i>
                                        Hồ sơ cá nhân
                                    </Link>
                                    <Link to="/membership" className="tamcare-dropdown-link" onClick={() => setShowUserDropdown(false)}>
                                        <i className="fa-solid fa-gem" style={{ color: '#f39c12' }}></i>
                                        Nâng cấp hội viên
                                    </Link>
                                    {!isAdmin && (
                                        <Link to="/shop/orders" className="tamcare-dropdown-link" onClick={() => setShowUserDropdown(false)}>
                                            <i className="fa-solid fa-receipt" style={{ color: '#2c5282' }}></i>
                                            Đơn hàng của tôi
                                        </Link>
                                    )}
                                    <button
                                        className="tamcare-dropdown-link"
                                        style={{ color: '#ef4444', borderTop: '1px solid #f1f5f9', background: 'none', border: 'none', width: '100%', cursor: 'pointer' }}
                                        onClick={handleLogout}
                                    >
                                        <i className="fa-solid fa-power-off"></i>
                                        Đăng xuất
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <nav className={`tamcare-nav-menu ${isMobileMenuOpen ? 'is-open' : ''}`} aria-label="Menu chính">
                    {!isAdmin && (
                        <Link
                            to={isElderly ? '/home_elderly' : '/home_caregiver'}
                            className={`tamcare-nav-link ${isActive(isElderly ? '/home_elderly' : '/home_caregiver')}`}
                        >
                            <i className="fa-solid fa-house"></i>
                            <span>Tổng quan</span>
                        </Link>
                    )}

                    {isLoggedIn && (isRelative || isElderly) && (
                        <Link to="/my-relatives" className={`tamcare-nav-link ${isActive('/my-relatives')}`}>
                            <i className="fa-solid fa-users"></i>
                            <span>Gia đình</span>
                        </Link>
                    )}

                    {(isRelative || isElderly) && renderNavLink('/medical-history', FEATURES.MEDICAL_RECORDS, 'fa-solid fa-notes-medical', 'Hồ sơ bệnh án')}

                    {(isRelative || isElderly) && renderNavLink(
                        isElderly ? `/medication-checklist/${data?.id}` : '/medication-checklist',
                        FEATURES.MEDICATION,
                        'fa-solid fa-pills',
                        'Lịch uống thuốc'
                    )}

                    {(isRelative || isElderly) && renderNavLink('/health', FEATURES.HEALTH_MONITOR, 'fa-solid fa-heart-pulse', 'Chỉ số')}

                    {!isAdmin && (
                        <>
                            <Link to="/blogs" className={`tamcare-nav-link ${isActive('/blogs')}`}>
                                <i className="fa-solid fa-newspaper"></i>
                                <span>Bài viết</span>
                            </Link>
                            {isLoggedIn && (isRelative || isElderly) && renderNavLink('/shop/products', FEATURES.SHOP, 'fa-solid fa-store', 'Cửa hàng')}
                        </>
                    )}

                    {isAdmin && (
                        <>
                            <Link to="/admin/dashboard" className={`tamcare-nav-link ${isActive('/admin/dashboard')}`}>
                                <i className="fa-solid fa-chart-line"></i>
                                <span>Dashboard</span>
                            </Link>
                            <Link to="/admin/users" className={`tamcare-nav-link ${isActive('/admin/users')}`}>
                                <i className="fa-solid fa-users-gear"></i>
                                <span>Người dùng</span>
                            </Link>
                            <Link to="/admin/blogs" className={`tamcare-nav-link ${isActive('/admin/blogs')}`}>
                                <i className="fa-solid fa-newspaper"></i>
                                <span>Bài viết</span>
                            </Link>
                            <Link to="/admin/shop" className={`tamcare-nav-link ${isActive('/admin/shop')}`}>
                                <i className="fa-solid fa-store"></i>
                                <span>Cửa hàng</span>
                            </Link>
                            <Link to="/admin/health-logs" className={`tamcare-nav-link ${isActive('/admin/health-logs')}`}>
                                <i className="fa-solid fa-heart-pulse"></i>
                                <span>Sức khỏe</span>
                            </Link>
                            <Link to="/admin/alerts" className={`tamcare-nav-link ${isActive('/admin/alerts')}`}>
                                <i className="fa-solid fa-triangle-exclamation"></i>
                                <span>Cảnh báo</span>
                            </Link>
                        </>
                    )}
            </nav>

            <div className={`tamcare-header-actions ${isMobileMenuOpen ? 'is-open' : ''}`}>
                {isLoggedIn && !isAdmin && !isCompact && (
                    <Link to="/shop/cart" className="tamcare-cart-btn" aria-label="Giỏ hàng">
                        <i className="fa-solid fa-cart-shopping"></i>
                        {itemsInCart > 0 && <div className="tamcare-noti-dot">{itemsInCart}</div>}
                    </Link>
                )}
                {!isCompact && isLoggedIn && (
                    <div
                        className="tamcare-header-item-wrapper"
                        onMouseEnter={!isCompact ? () => setShowNotiDropdown(true) : undefined}
                        onMouseLeave={!isCompact ? () => setShowNotiDropdown(false) : undefined}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="tamcare-noti-btn"
                            onClick={() => {
                                if (isCompact) {
                                    setShowNotiDropdown((prev) => !prev);
                                    setShowUserDropdown(false);
                                }
                            }}
                        >
                            <i className="fa-solid fa-bell"></i>
                            {unreadCount > 0 && <div className="tamcare-noti-dot">{unreadCount}</div>}
                        </button>
                        {showNotiDropdown && (
                            <div className="tamcare-dropdown-box">
                                <div className="tamcare-noti-header">Thông báo mới</div>
                                <div className="tamcare-noti-scroll">
                                    {alertsError ? (
                                        <div className="tamcare-noti-item">
                                            <strong>Lỗi</strong>
                                            <p>{alertsError}</p>
                                        </div>
                                    ) : alerts.length === 0 ? (
                                        <div className="tamcare-noti-item">
                                            <strong>Không có thông báo mới</strong>
                                            <p>Hệ thống sẽ hiển thị nhắc uống thuốc và cảnh báo tại đây.</p>
                                        </div>
                                    ) : (
                                        alerts.map((alert) => renderAlertItem(alert))
                                    )}
                                </div>
                                <div className="tamcare-noti-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '8px' }}>
                                    <Link
                                        to="/alerts"
                                        className="tamcare-dropdown-link"
                                        style={{ width: '100%', textAlign: 'center', display: 'block' }}
                                        onClick={() => { setShowNotiDropdown(false); }}
                                    >
                                        <i className="fa-regular fa-bell" style={{ marginRight: 6 }}></i>
                                        Xem chi tiết
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {!isCompact && isLoggedIn ? (
                    <div
                        className="tamcare-header-item-wrapper"
                        onMouseEnter={!isCompact ? () => setShowUserDropdown(true) : undefined}
                        onMouseLeave={!isCompact ? () => setShowUserDropdown(false) : undefined}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="tamcare-user-pill"
                            onClick={() => {
                                if (isCompact) {
                                    setShowUserDropdown((prev) => !prev);
                                    setShowNotiDropdown(false);
                                }
                            }}
                        >
                            <span>{data?.fullName || data?.username || 'Người dùng'}</span>
                            <div className="tamcare-user-avatar">
                                {me?.avatar ? (
                                    <img src={`${API_BASE}${me.avatar}`} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                ) : (
                                    <i className="fa-solid fa-user"></i>
                                )}
                            </div>
                        </button>
                        {showUserDropdown && (
                            <div className="tamcare-dropdown-box" style={{ width: '280px' }}>
                                <div className="tamcare-profile-info">
                                    <div className="tamcare-avatar-large" style={{ overflow: 'hidden' }}>
                                        {me?.avatar ? (
                                            <img src={`${API_BASE}${me.avatar}`} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                        ) : (
                                            profileInitial
                                        )}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '700', color: '#2c5282', fontSize: '14px' }}>
                                            {data?.fullName || data?.username}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.4' }}>
                                            {membershipBadge}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ padding: '10px 0' }}>
                                    <Link to="/profile" className="tamcare-dropdown-link">
                                        <i className="fa-solid fa-address-card" style={{ color: '#6366f1' }}></i>
                                        Hồ sơ cá nhân
                                    </Link>
                                    <Link to="/membership" className="tamcare-dropdown-link">
                                        <i className="fa-solid fa-gem" style={{ color: '#f39c12' }}></i>
                                        Nâng cấp hội viên
                                    </Link>
                                    {!isAdmin && (
                                        <Link to="/shop/orders" className="tamcare-dropdown-link" onClick={() => setShowUserDropdown(false)}>
                                            <i className="fa-solid fa-receipt" style={{ color: '#2c5282' }}></i>
                                            Đơn hàng của tôi
                                        </Link>
                                    )}
                                    <button
                                        className="tamcare-dropdown-link"
                                        style={{ color: '#ef4444', borderTop: '1px solid #f1f5f9', background: 'none', border: 'none', width: '100%', cursor: 'pointer' }}
                                        onClick={handleLogout}
                                    >
                                        <i className="fa-solid fa-power-off"></i>
                                        Đăng xuất
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : isCompact && isLoggedIn ? (
                    <>
                        <Link to="/profile" className="tamcare-nav-link tamcare-account-link" onClick={() => setIsMobileMenuOpen(false)}>
                            <i className="fa-solid fa-address-card"></i>
                            <span>Hồ sơ cá nhân</span>
                        </Link>
                        {!isAdmin && (
                            <Link to="/shop/orders" className="tamcare-nav-link tamcare-account-link" onClick={() => setIsMobileMenuOpen(false)}>
                                <i className="fa-solid fa-receipt"></i>
                                <span>Đơn hàng của tôi</span>
                            </Link>
                        )}
                        <Link to="/profile" className="tamcare-nav-link tamcare-account-link" onClick={() => setIsMobileMenuOpen(false)}>
                            <i className="fa-solid fa-user"></i>
                            <span>{data?.fullName || data?.username || 'Người dùng'}</span>
                        </Link>
                        <button
                            type="button"
                            className="tamcare-nav-link tamcare-account-link tamcare-account-link--danger"
                            onClick={handleLogout}
                        >
                            <i className="fa-solid fa-power-off"></i>
                            <span>Đăng xuất</span>
                        </button>
                    </>
                ) : (
                    <>
                        <a
                            href={APK_DOWNLOAD_URL}
                            className="tamcare-btn-download"
                            download="TamCare.apk"
                        >
                            <i className="fa-brands fa-android" aria-hidden="true"></i>
                            Tải ứng dụng
                        </a>
                        <Link to="/login" className="tamcare-btn-ghost">
                            Đăng nhập
                        </Link>
                        <Link to="/register" className="tamcare-btn-primary">
                            Đăng ký
                        </Link>
                    </>
                )}
            </div>
            {followupModal && (
                <div className="tamcare-followup-overlay" role="dialog" aria-modal="true" onClick={() => setFollowupModal(null)}>
                    <div className="tamcare-followup-card" onClick={(e) => e.stopPropagation()}>
                        <div className="tamcare-followup-header">
                            <h3>Trợ lý AI</h3>
                            <button type="button" className="tamcare-followup-close" onClick={() => setFollowupModal(null)}>
                                ×
                            </button>
                        </div>
                        <p className="tamcare-followup-question">Cụ uống thuốc được 15 phút rồi, cụ cảm thấy thế nào ạ?</p>
                        <div className="tamcare-followup-grid">
                            <button type="button" className="tamcare-followup-btn" disabled={followupSubmitting} onClick={() => submitFollowupReaction(followupModal, 'normal')}>
                                😀 Bình thường
                            </button>
                            <button type="button" className="tamcare-followup-btn" disabled={followupSubmitting} onClick={() => submitFollowupReaction(followupModal, 'nausea')}>
                                🤢 Buồn nôn
                            </button>
                            <button type="button" className="tamcare-followup-btn" disabled={followupSubmitting} onClick={() => submitFollowupReaction(followupModal, 'dizzy')}>
                                😵 Chóng mặt
                            </button>
                            <button type="button" className="tamcare-followup-btn" disabled={followupSubmitting} onClick={() => submitFollowupReaction(followupModal, 'tachycardia')}>
                                ❤️ Tim đập nhanh
                            </button>
                        </div>
                        {followupSubmitting && (
                            <p className="tamcare-followup-question" style={{ marginTop: 12, marginBottom: 0 }}>
                                Đang ghi nhận phản ứng...
                            </p>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
